
import { ValidationProvider } from 'vee-validate';
import Bugsnag from '@bugsnag/js';

import GwPageTitle from '~/patterns/molecules/page-title/page-title.vue';
import GwPageContainer from '~/patterns/molecules/page-container/page-container.vue';
import GwIcon from '~/patterns/atoms/icon/icon.vue';
import GwRadio from '~/patterns/atoms/radio/radio.vue';
import GwCheckbox from '~/patterns/atoms/checkbox/checkbox.vue';
import GwInput from '~/patterns/atoms/input/input.vue';
import GwCountrySelect from '~/patterns/atoms/multiselect/presets/countryselect.vue';
import GwStateSelect from '~/patterns/atoms/multiselect/presets/stateselect.vue';
import GwModal from '~/patterns/atoms/modal/modal.vue';
import GwFormField from '~/patterns/molecules/form-field/form-field.vue';
import GwButton from '~/patterns/atoms/button/button.vue';
import GwFormSection from '~/patterns/molecules/form-section/form-section.vue';
import GwMultistepForm from '~/patterns/organisms/multistep-form/multistep-form.vue';
import GwOrderSummary from '~/patterns/organisms/order-summary/order-summary.vue';
import GwOrderInformation from '~/patterns/molecules/order-information/order-information.vue';
import GwPriceBreakdown from '~/patterns/molecules/price-breakdown/price-breakdown.vue';
import GwPaymentMethods from '~/patterns/molecules/payment-methods/payment-methods.vue';
import GwPaymentDropIn from '~/patterns/molecules/payment-drop-in/payment-drop-in.vue';
import GwCheckout from '~/patterns/organisms/checkout/checkout.vue';
import GwCouponCode from '~/patterns/molecules/coupon-code/coupon-code.vue';

import trackTransaction from '~/mixins/track-transaction';
import GwPaymentProviders from '~/patterns/molecules/payment-providers/payment-providers.vue';
import GwUsps from '~/patterns/molecules/usps/usps.vue';
import addCurrency from '~/mixins/addCurrency';
import CrossSellOverview from '~/components/CrossSellOverview/CrossSellOverview';
import getCrossSellEditions from '~/graphql/queries/getCrossSellEditions.graphql';

import seo from '~/mixins/seo';

export default {
    components: {
        CrossSellOverview,
        ValidationProvider,
        GwPageContainer,
        GwPageTitle,
        GwIcon,
        GwInput,
        GwCountrySelect,
        GwStateSelect,
        GwRadio,
        GwCheckbox,
        GwFormField,
        GwButton,
        GwModal,
        GwFormSection,
        GwMultistepForm,
        GwOrderSummary,
        GwOrderInformation,
        GwPriceBreakdown,
        GwCheckout,
        GwPaymentMethods,
        GwPaymentDropIn,
        GwCouponCode,
        GwPaymentProviders,
        GwUsps
    },

    mixins: [trackTransaction, addCurrency, seo],

    layout: 'checkout',

    data() {
        return {
            edition: {
                id: null,
            },
            editions: [],
            editionInfo: {},
            paymentMethod: null,
            crossSellEditions: [],
            price: {},
            participants: [
                {
                    id: this.uniqueId(),
                    firstName: null,
                    lastName: null,
                    email: null,
                    phone: null,
                    jobTitle: null
                }
            ],
            customer: {
                type: 'corporate',
                company: null,
                vatNumber: null,
                firstName: null,
                lastName: null,
                email: null,
                phone: null,
                jobTitle: null,
                address: null,
                zipcode: null,
                city: null,
                country: null,
                state: null,
                reference: null
            },
            ccAvenueConfig: {
                url: null,
                encryptedData: null,
                accessCode: null,
            },
            dropIn: null,
            showModal: false,
            showError: false,
            error: null,
            orderPending: false,
            currency: null,
            couponCode: null,
            reuseParticipantInformation: false,
            filters: {
                site: this.$config.appMultisiteHandle
            }
        };
    },

    computed: {
        numberOfParticipants() {
            return this.participants.length;
        },
        paymentMethodSelected() {
            return this.paymentMethod !== null;
        },
        hasCrossSellEditions() {
            return this.crossSellEditions.length > 0;
        },
        customerType() {
            return this.customer.type;
        },
        isIndia() {
            return this.$config.appMultisiteHandle === 'in';
        },
        orderDetails() {
            return {
                customer: {
                    firstName: this.customer.firstName,
                    lastName: this.customer.lastName,
                    email: this.customer.email,
                    phone: this.customer.phone,
                    jobTitle: this.customer.jobTitle,
                    address: this.customer.address,
                    zipcode: this.customer.zipcode,
                    city: this.customer.city,
                    country: this.customer.country,
                    state: this.customer.state,
                },
                currency: this.currency,
                id: this.edition.id,
                net: this.price.net ?? this.price.totalNet,
                vatAmount: this.price.vatAmount ?? this.price.totalVat,
                title: this.editionInfo.title,
                location: this.editionInfo?.location ?? this.editionInfo?.locations?.[0]?.title,
                gross: this.price.gross ?? this.price.totalGross,
                quantity: this.participants.length,
                trainers: this.editionInfo.trainers,
                startDate: this.editionInfo.startDate,
                paymentMethod: this.paymentMethod
            };
        }
    },
    watch: {
        customerType() {
            if (this.customer.type === 'personal') {
                this.customer.vatNumber = null;
            }
        },
        reuseParticipantInformation(value) {
            if (value === true) {
                Object.assign(this.customer, this.participants[0]);
            } else {
                const empty = {
                    firstName: '',
                    lastName: '',
                    email: '',
                    phone: ''
                };

                Object.assign(this.customer, empty);
            }
        }
    },
    created() {
        this.edition.id = this.$route.query.id || '';

        this.editions = [{ id: this.edition.id, primaryEdition: true }];

        this.getCrossSellEditions();

        if (this.isIndia) {
            this.customer.type = 'personal';
            this.customer.vatNumber = null;
        }
    },
    beforeMount() {
        const localCurrency = localStorage.getItem('selectedCurrency');
        this.currency = (localCurrency !== null) ? localCurrency : this.$config.site.currencies[0];
    },
    mounted() {
        this.$nuxt.$on('priceUpdate', (payload) => {
            // Todo add typings for price payload
            this.price = payload;
        });
        this.$nuxt.$on('editionUpdate', (payload) => {
            // Todo add typings for edition payload
            this.editionInfo = payload;
        });
        this.$root.$on('update-editions', this.updateEditions);
    },
    methods: {
        uniqueId() {
            return Math.random().toString(16).slice(2);
        },
        addParticipant() {
            const emptyParticipant = {
                id: this.uniqueId(),
                firstName: null,
                lastName: null,
                email: null,
                phone: null,
                jobTitle: null
            };

            this.participants.push(emptyParticipant);
        },
        removeParticipant(index) {
            this.participants.splice(index, 1);
        },
        getParticipantCount(item, index) {
            if (this.editions?.[index]?.primaryEdition) {
                return this.participants.length;
            }

            if (this.editions?.[index]?.participants) {
                return this.editions[index].participants.length;
            }

            return 1;
        },
        calculateCrossSellDiscount(item) {
            if (!this.crossSellEditions?.length) {
                return 0;
            }

            return this.crossSellEditions?.find(edition => edition.id === item.editionId)?.crossSellPercentage;
        },
        setPaymentMethod(method) {
            this.paymentMethod = method;
        },
        initCCAvenue(config) {
            this.ccAvenueConfig = config;

            this.$nextTick(() => {
                this.$refs.ccAvenueForm.submit();
            });
        },
        async initAdyen(config) {
            config.onPaymentCompleted = async(result, component) => {
                // eslint-disable-next-line no-console
                console.info(result, component);

                if (result.resultCode === 'Authorised') {
                    await this.trackTransaction(this.enrichOrderDetails(this.orderDetails));

                    this.$router.push({
                        path: '/checkout/confirmation'
                    });

                    return;
                }

                this.error = result.resultCode;
            };
            config.onError = (error, component) => {
                // eslint-disable-next-line no-console
                console.error(error.name, error.message, error.stack, component);

                Bugsnag.notify(error);

                this.error = error.message;
            };

            const { default: AdyenCheckout } = await import('@adyen/adyen-web');
            const checkout = await AdyenCheckout(config);
            const method = this.paymentMethod.replace('adyen-', '');

            this.showModal = true;

            this.$nextTick(() => {
                // Create an instance of Drop-in and mount it to the container you created.
                this.dropIn = checkout.create(method).mount('#dropin-container');
            });
        },
        updateCountry(option) {
            if (option) {
                this.customer.country = option.code;

                this.$nextTick(() => {
                    this.$refs.customerCountry.validate();
                });
            } else {
                this.customer.country = null;

                this.$nextTick(() => {
                    this.$refs.customerCountry.validate();
                });
            }
        },
        updateState(option) {
            if (option) {
                this.customer.state = option.code;

                this.$nextTick(() => {
                    this.$refs.customerCountry.validate();
                });
            } else {
                this.customer.state = null;

                this.$nextTick(() => {
                    this.$refs.customerState.validate();
                });
            }
        },
        submitAdyen() {
            this.dropIn.submit();
        },
        updateEditions(mode, editionId, selectedParticipants) {
            const exist = this.editions.find(edition => edition.id === editionId);

            // if edition already exist, remove from array
            if (exist !== undefined && mode === 'remove') {
                this.editions = this.editions.filter(item => item.id !== editionId);
                return;
            }

            if (exist !== undefined && mode === 'update') {
                this.editions.find(edition => edition.id === editionId).participants = selectedParticipants;
                return;
            }

            // if edition doesn't exist, add to array
            this.editions.push({
                id: editionId,
                participants: selectedParticipants,
                primaryEdition: false
            });
        },
        updateParticipants(participant, index) {
            this.participants[index] = participant;
        },
        addCouponCode(code) {
            this.couponCode = code;
        },

        enrichOrderDetails(details) {
            return {
                ...details,
                url: window.location.href || null,
                location: this.editionInfo?.location || this.editionInfo?.locations?.[0]?.title || null,
                startDate: this.editionInfo?.start || null
            };
        },

        async getCrossSellEditions() {
            this.filters.id = [
                'not', this?.edition?.id
            ];
            this.filters.limit = 4;
            this.filters.administration = this.editionInfo.administration;

            const filters = this.filters;

            const { data } = await this.$gql.executeQuery(getCrossSellEditions, { ...filters });

            this.crossSellEditions = [...data.entries];
        },

        storeOrderDetails(sessionId) {
            localStorage.setItem(`orderDetails-${sessionId}`, JSON.stringify(this.orderDetails));
        },

        async submitOrder() {
            this.orderPending = true;

            const orderData = {
                paymentType: this.paymentMethod,
                editions: this.editions,
                participants: this.participants,
                customer: {
                    firstName: this.customer.firstName,
                    lastName: this.customer.lastName,
                    email: this.customer.email,
                    phone: this.customer.phone,
                    jobTitle: this.customer.jobTitle,
                    address: this.customer.address,
                    zipcode: this.customer.zipcode,
                    city: this.customer.city,
                    country: this.customer.country,
                    state: this.customer.state,
                    reference: this.customer.reference
                },
                returnUrl: `${this.$config.baseURL}/checkout/confirmation`,
                currency: this.currency,
                site: this.$config.appMultisiteHandle
            };

            if (this.couponCode) {
                orderData.couponCode = this.couponCode;
            }

            if (this.customer.type === 'corporate') {
                orderData.customer.company = this.customer.company;
                orderData.customer.reference = this.customer.reference;

                if (this.customer.vatNumber !== null) {
                    orderData.customer.vatNumber = this.customer.vatNumber.replaceAll(/(?:[-,.!?:; ])/gm, '');
                }
            }

            try {
                await this.$axios.post(this.$config.apiUrl + '/api/orders', orderData)
                    .then(async({ data }) => {
                        // This works, only for 'pay by invoice' payment method
                        if (data.completed) {
                            await this.trackTransaction(this.enrichOrderDetails(this.orderDetails));

                            this.$router.push({
                                path: '/checkout/confirmation'
                            });

                            return;
                        }

                        // Handle CCAvenue payment method (India only)
                        // Broken?
                        if (data.action === 'cc-avenue') {
                            this.storeOrderDetails(data.config.session.orderId);
                            this.initCCAvenue(data.config.session);

                            return;
                        }

                        // Handle Adyen payment methods
                        // THIS IS BROKEN
                        if (data.config.session.id) {
                            // Store order details in localStorage for enhanced ecommerce tracking
                            this.storeOrderDetails(data.config.session.id);
                        }
                        this.initAdyen(data.config);
                        this.error = null;
                    });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);

                Bugsnag.notify(error, (event) => {
                    event.addMetadata('customer', {
                        orderData
                    });
                });

                if (error.response?.data?.message) {
                    this.error = error.response.data.message;
                }
                this.showError = true;
            }

            this.orderPending = false;
        }
    }
};
